import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import ConfirmationDialog from '../../components/ConfirmationDialog'
import Button from '../../components/Button'
import { useAuth } from '../../context/AuthContext'
import { useRecipe, GroceryListWithItems, GroceryListSummary } from '../../context/RecipeContext'
import styles from './GroceryList.module.css'
import { usePageSEO } from '../../hooks/usePageSEO'

const GroceryList: React.FC = () => {
  return <GroceryListContent />
}

const GroceryListContent: React.FC = () => {
  // SEO hook for the grocery list page
  usePageSEO({
    title: "Smart Grocery Lists for Your Diet | PlatefullAI - Personalized Food Shopping",
    description: "Create personalized grocery lists based on your dietary preferences and restrictions. Smart shopping lists that respect your digital diet identity and help you find foods that match your nutritional needs.",
    keywords: "personalized grocery lists, dietary preferences, smart shopping, food allergies, dietary restrictions, personalized nutrition, digital diet identity, meal planning, food technology, dietary shopping, nutritional needs",
    canonicalUrl: "https://platefullai.com/grocery-list"
  });

  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { updateGroceryItem, deleteGroceryItem, deleteGroceryList, getGroceryListsSummary, copyGroceryList, getGroceryListWithItems } = useRecipe()
  const [groceryList, setGroceryList] = useState<GroceryListWithItems | null>(null)
  const [pastLists, setPastLists] = useState<GroceryListSummary[]>([])
  const [isLoadingPastLists, setIsLoadingPastLists] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [listToDelete, setListToDelete] = useState<{id: string, name: string} | null>(null)
  const [deletingListId, setDeletingListId] = useState<string | null>(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const handleLogin = () => {
    // TODO: Navigate to login page or show login modal
    console.log('Login clicked');
  };

  useEffect(() => {
    // Load grocery list from localStorage
    const stored = localStorage.getItem('currentGroceryList')
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        setGroceryList(parsed)
      } catch (error) {
        console.error('Error parsing grocery list from localStorage:', error)
        setGroceryList(null)
      }
    }
  }, [])

  const loadPastLists = useCallback(async () => {
    setIsLoadingPastLists(true)
    try {
      const lists = await getGroceryListsSummary()
      // Filter out the current list if it exists in the database
      const currentListId = groceryList?.id
      const filteredLists = currentListId 
        ? lists.filter(list => list.id !== currentListId)
        : lists
      setPastLists(filteredLists)
    } catch (error) {
      console.error('Error loading past grocery lists:', error)
    } finally {
      setIsLoadingPastLists(false)
    }
  }, [getGroceryListsSummary, groceryList?.id])

  // Load past grocery lists when user is available
  useEffect(() => {
    if (user) {
      loadPastLists()
    }
  }, [user, loadPastLists])

  const toggleItem = async (itemId: string) => {
    if (!groceryList) return
    
    const item = groceryList.items.find(item => item.id === itemId)
    if (!item) return
    
    try {
      // Update in backend
      const updatedItem = await updateGroceryItem(groceryList.id, itemId, { 
        is_checked: !item.is_checked 
      })
      
      if (updatedItem) {
        // Update local state
        setGroceryList(prev => prev ? {
          ...prev,
          items: prev.items.map(item => 
            item.id === itemId ? { ...item, is_checked: !item.is_checked } : item
          )
        } : null)
        
        // Update localStorage
        const updatedList = {
          ...groceryList,
          items: groceryList.items.map(item => 
            item.id === itemId ? { ...item, is_checked: !item.is_checked } : item
          )
        }
        localStorage.setItem('currentGroceryList', JSON.stringify(updatedList))
      }
    } catch (error) {
      console.error('Error updating grocery item:', error)
      alert('Failed to update item')
    }
  }

  const deleteItem = async (itemId: string) => {
    if (!groceryList) return
    
    try {
      // Delete from backend
      await deleteGroceryItem(groceryList.id, itemId)
      
      // Update local state
      setGroceryList(prev => prev ? {
        ...prev,
        items: prev.items.filter(item => item.id !== itemId)
      } : null)
      
      // Update localStorage
      const updatedList = {
        ...groceryList,
        items: groceryList.items.filter(item => item.id !== itemId)
      }
      localStorage.setItem('currentGroceryList', JSON.stringify(updatedList))
    } catch (error) {
      console.error('Error deleting grocery item:', error)
      alert('Failed to delete item')
    }
  }

  const clearChecked = () => {
    if (!groceryList) return
    
    // Remove checked items from localStorage
    const updatedList = {
      ...groceryList,
      items: groceryList.items.filter(item => !item.is_checked)
    }
    localStorage.setItem('currentGroceryList', JSON.stringify(updatedList))
    setGroceryList(updatedList)
  }

  const clearAll = () => {
    setGroceryList(null)
    localStorage.removeItem('currentGroceryList')
  }

  const handleViewPastList = async (listId: string) => {
    try {
      const listWithItems = await getGroceryListWithItems(listId)
      if (listWithItems) {
        setGroceryList(listWithItems)
        localStorage.setItem('currentGroceryList', JSON.stringify(listWithItems))
        // Refresh past lists to update the filter
        loadPastLists()
      }
    } catch (error) {
      console.error('Error loading past grocery list:', error)
    }
  }

  const handleCopyPastList = async (listId: string, listName: string) => {
    const newName = prompt(`Copy "${listName}" as:`, `${listName} (Copy)`)
    if (!newName) return

    try {
      const newList = await copyGroceryList(listId, newName, false)
      if (newList) {
        // Load the new list with items and set as current
        const newListWithItems = await getGroceryListWithItems(newList.id)
        if (newListWithItems) {
          setGroceryList(newListWithItems)
          localStorage.setItem('currentGroceryList', JSON.stringify(newListWithItems))
          // Refresh past lists
          loadPastLists()
        }
      }
    } catch (error) {
      console.error('Error copying grocery list:', error)
    }
  }

  const handleDeleteList = (listId: string, listName: string) => {
    setListToDelete({ id: listId, name: listName })
    setShowDeleteDialog(true)
  }

  const handleConfirmDelete = async () => {
    if (!listToDelete) return

    try {
      // Start the deletion animation
      setDeletingListId(listToDelete.id)
      setShowDeleteDialog(false)
      
      // Wait a bit for the animation to start
      await new Promise(resolve => setTimeout(resolve, 100))
      
      // Perform the actual deletion
      await deleteGroceryList(listToDelete.id)
      
      // If we deleted the current list, clear it
      if (groceryList && groceryList.id === listToDelete.id) {
        setGroceryList(null)
        localStorage.removeItem('currentGroceryList')
      }
      
      // Show success message
      setSuccessMessage(`"${listToDelete.name}" deleted successfully`)
      setShowSuccessMessage(true)
      
      // Wait for fade-out animation to complete
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // Refresh past lists and clean up
      loadPastLists()
      setDeletingListId(null)
      setListToDelete(null)
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessMessage(false)
      }, 3000)
      
    } catch (error) {
      console.error('Error deleting grocery list:', error)
      setDeletingListId(null)
      alert('Failed to delete grocery list. Please try again.')
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteDialog(false)
    setListToDelete(null)
  }

  const checkedCount = groceryList?.items.filter(item => item.is_checked).length || 0
  const totalCount = groceryList?.items.length || 0

  if (!user) {
    return (
      <div className={styles.container}>
        <Header user={user} onLogin={handleLogin} onLogout={logout} />
        <div className={styles.content}>
          <div className={styles.emptyState}>
            <h2>Sign in to manage your grocery list</h2>
            <p>Create and manage grocery lists from your favorite recipes</p>
            <p>Use the sign-in button in the header above to get started.</p>
          </div>
        </div>
      </div>
    )
  }

  if (!groceryList || groceryList.items.length === 0) {
    return (
      <div className={styles.container}>
        <Header user={user} onLogin={handleLogin} onLogout={logout} />
        <div className={styles.content}>
          <div className={styles.emptyState}>
            <h2>Your grocery list is empty</h2>
            <p>Browse recipes and add ingredients to start building your grocery list</p>
            <Button variant="primary" onClick={() => navigate('/recipes')}>
              Browse Recipes
            </Button>
          </div>

          {/* Past Grocery Lists Section - Always show */}
          <div className={styles.pastListsSection}>
            <h2 className={styles.pastListsTitle}>Past Grocery Lists</h2>
            {isLoadingPastLists ? (
              <div className={styles.loading}>Loading past lists...</div>
            ) : pastLists.length > 0 ? (
              <div className={styles.pastListsGrid}>
                {pastLists.map((list) => (
                  <div 
                    key={list.id} 
                    className={`${styles.pastListCard} ${deletingListId === list.id ? styles.deleting : ''}`}
                  >
                    <div className={styles.pastListHeader}>
                      <h3 className={styles.pastListName}>{list.name}</h3>
                      <div className={styles.pastListDate}>
                        {new Date(list.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    
                    <div className={styles.pastListStats}>
                      <div className={styles.pastListStat}>
                        <span className={styles.statNumber}>{list.total_items}</span>
                        <span className={styles.statLabel}>items</span>
                      </div>
                      <div className={styles.pastListStat}>
                        <span className={styles.statNumber}>{list.completion_percentage}%</span>
                        <span className={styles.statLabel}>completed</span>
                      </div>
                    </div>

                    {list.last_recipe_added && (
                      <div className={styles.lastRecipe}>
                        Last recipe: {list.last_recipe_added}
                      </div>
                    )}

                    <div className={styles.pastListActions}>
                      <Button 
                        variant="secondary"
                        size="small"
                        onClick={() => handleViewPastList(list.id)}
                        className={styles.pastListButton}
                      >
                        View
                      </Button>
                      <Button 
                        variant="secondary"
                        size="small"
                        onClick={() => handleCopyPastList(list.id, list.name)}
                        className={styles.pastListButton}
                      >
                        Copy
                      </Button>
                      <Button 
                        variant="secondary"
                        size="small"
                        onClick={() => handleDeleteList(list.id, list.name)}
                        className={`${styles.pastListButton} ${styles.deleteButtonText}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                <p>No past grocery lists found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <Header user={user} onLogin={handleLogin} onLogout={logout} />
      <div className={styles.content}>
        <div className={styles.header}>
          <h1>Grocery List</h1>
          <div className={styles.stats}>
            <span>{checkedCount} of {totalCount} items checked</span>
          </div>
        </div>

        <div className={styles.actions}>
          <Button 
            variant="secondary" 
            onClick={clearChecked}
            disabled={checkedCount === 0}
          >
            Clear Checked
          </Button>
          <Button 
            variant="secondary" 
            onClick={clearAll}
            disabled={totalCount === 0}
          >
            Close List
          </Button>
          <Button 
            variant="primary" 
            onClick={() => navigate('/')}
          >
            Add More Recipes
          </Button>
        </div>

        <div className={styles.list}>
          {groceryList.items.map((item) => (
            <div key={item.id} className={`${styles.item} ${item.is_checked ? styles.checked : ''}`}>
              <div 
                className={styles.itemContent}
                onClick={() => toggleItem(item.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggleItem(item.id)
                  }
                }}
                aria-label={`Toggle ${item.name} - ${item.is_checked ? 'checked' : 'unchecked'}`}
              >
                <input
                  type="checkbox"
                  checked={item.is_checked}
                  onChange={() => toggleItem(item.id)}
                  className={styles.checkbox}
                  onClick={(e) => e.stopPropagation()} // Prevent double-triggering
                />
                <div className={styles.itemDetails}>
                  <span className={styles.itemName}>{item.name}</span>
                  {item.quantity_value && item.quantity_unit && (
                    <span className={styles.quantity}>
                      {item.quantity_value} {item.quantity_unit}
                    </span>
                  )}
                  {item.note && <span className={styles.note}>{item.note}</span>}
                </div>
              </div>
              <button 
                className={styles.deleteButton}
                onClick={(e) => {
                  e.stopPropagation() // Prevent triggering the toggle
                  deleteItem(item.id)
                }}
                aria-label="Delete item"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        {/* Past Grocery Lists Section - Always show */}
        <div className={styles.pastListsSection}>
          <h2 className={styles.pastListsTitle}>Past Grocery Lists</h2>
          {isLoadingPastLists ? (
            <div className={styles.loading}>Loading past lists...</div>
          ) : pastLists.length > 0 ? (
            <div className={styles.pastListsGrid}>
              {pastLists.map((list) => (
                <div 
                  key={list.id} 
                  className={`${styles.pastListCard} ${deletingListId === list.id ? styles.deleting : ''}`}
                >
                  <div className={styles.pastListHeader}>
                    <h3 className={styles.pastListName}>{list.name}</h3>
                    <div className={styles.pastListDate}>
                      {new Date(list.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  
                  <div className={styles.pastListStats}>
                    <div className={styles.pastListStat}>
                      <span className={styles.statNumber}>{list.total_items}</span>
                      <span className={styles.statLabel}>items</span>
                    </div>
                    <div className={styles.pastListStat}>
                      <span className={styles.statNumber}>{list.completion_percentage}%</span>
                      <span className={styles.statLabel}>completed</span>
                    </div>
                  </div>

                  {list.last_recipe_added && (
                    <div className={styles.lastRecipe}>
                      Last recipe: {list.last_recipe_added}
                    </div>
                  )}

                  <div className={styles.pastListActions}>
                    <Button 
                      variant="secondary"
                      size="small"
                      onClick={() => handleViewPastList(list.id)}
                      className={styles.pastListButton}
                    >
                      View
                    </Button>
                    <Button 
                      variant="secondary"
                      size="small"
                      onClick={() => handleCopyPastList(list.id, list.name)}
                      className={styles.pastListButton}
                    >
                      Copy
                    </Button>
                    <Button 
                      variant="secondary"
                      size="small"
                      onClick={() => handleDeleteList(list.id, list.name)}
                      className={`${styles.pastListButton} ${styles.deleteButtonText}`}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <p>No past grocery lists found</p>
            </div>
          )}
        </div>

        {/* Success Message */}
        {showSuccessMessage && (
          <div className={styles.successMessage}>
            <div className={styles.successContent}>
              <span className={styles.successIcon}>✓</span>
              <span className={styles.successText}>{successMessage}</span>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && listToDelete && (
        <ConfirmationDialog
          isOpen={showDeleteDialog}
          title="Delete Grocery List"
          message={`Are you sure you want to delete "${listToDelete.name}"? This action cannot be undone.`}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </div>
  )
}

export default GroceryList
